import React, { useEffect, useState } from "react";
import { useDebounce } from "use-debounce";
// import { throttle, debounce } from "throttle-debounce";
import Downshift from "downshift";
import { fetch } from "whatwg-fetch";
import { endpoints } from "../../utils/api";
import { employerNameChanged, broadcast } from "../../utils/events";
import {
  setValueById,
  toggleHideContainers,
  toggleHideContainerById,
} from "../../utils/dom";

// see: https://geo.api.gouv.fr/adresse
export default function SirenInput({
  resourceName,
  currentSiret,
  railsEnv,
  newRecord,
  currentManualEnter,
  onSubmitError,
  lastPublicValue,
}) {
  const [siret, setSiret] = useState(currentSiret || "");
  const [searchResults, setSearchResults] = useState([]);
  const [debouncedSiret, setDebouncedSiret] = useState(siret);
  const [employerNameStr, setEmployerNameStr] = useState(currentSiret);
  const [internshipAddressManualEnter, setInternshipAddressManualEnter] =
    useState(currentManualEnter || false);
  const [isFaulty, setFaulty] = useState(onSubmitError || false);
  const [formerPublicValue, setFormerPublicValue] = useState(
    lastPublicValue || false
  );
  // MGF-1666 : message d'erreur DSFR affiché au blur (champ vide / SIRET invalide).
  const [blurError, setBlurError] = useState(null);

  const inputChange = (event) => {
    setSiret(event.target.value);
    setEmployerNameStr(event.target.value);
    setBlurError(null);
  };

  const handleBlur = () => {
    const cleanSiret = siret.replace(/\s/g, "");
    if (siret.trim().length === 0) {
      setBlurError(
        "Veuillez indiquer le nom ou le SIRET de la structure d'accueil"
      );
    } else if (
      /^\d+$/.test(cleanSiret) &&
      cleanSiret.length === 14 &&
      !isAValidSiret(cleanSiret)
    ) {
      setBlurError("Le SIRET saisi n'est pas valide");
    } else {
      setBlurError(null);
    }
  };

  const searchCompanyBySiret = (siret) => {
    fetch(endpoints.searchCompanyBySiret({ siret }))
      .then((response) => response.json())
      .then((json) => {
        if (json.etablissement !== undefined) {
          setSearchResults([json.etablissement]);
        } else {
          setSearchResults([]);
        }
      });
  };

  const searchCompanyByName = (name) => {
    fetch(endpoints.searchCompanyByName({ name }))
      .then((response) => response.json())
      .then((json) => {
        if (json.etablissements !== undefined) {
          setSearchResults(json.etablissements);
        } else {
          setSearchResults([]);
        }
      })
      .catch((err) => {
        toggleHideContainerById("siren-error", true);
      });
  };

  const siretPresentation = (siret) => {
    return siret.replace(/(\d{3})(\d{3})(\d{3})(\d{5})/, "$1 $2 $3 $4");
  };

  const searchCoordinatesByAddress = (fullAddress) => {
    const coordinates = `${resourceName}_entreprise_coordinates`;
    const selector_lon = `${coordinates}_longitude`;
    const selector_lat = `${coordinates}_latitude`;
    fetch(endpoints.apiSearchAddress({ fullAddress }))
      .then((response) => response.json())
      .then((json) => {
        document.getElementById(selector_lon).value =
          json.features[0].geometry.coordinates[0];
        document.getElementById(selector_lat).value =
          json.features[0].geometry.coordinates[1];
      });
  };

  const openManual = (event) => {
    event.preventDefault();
    setOpenManual();
  };

  // Active (public) ou désactive (privé) l'option "Fonction publique" du select de secteur.
  const toggleFonctionPubliqueOption = (sectorEl, available) => {
    if (!sectorEl || !sectorEl.options) return;
    for (let i = 0; i < sectorEl.options.length; i++) {
      const option = sectorEl.options[i];
      if (option.text.toLowerCase().includes("fonction publique")) {
        option.disabled = !available;
        option.hidden = !available;
        if (!available && sectorEl.value === option.value) {
          sectorEl.value = "";
        }
        break;
      }
    }
  };

  const setOpenManual = () => {
    setInternshipAddressManualEnter(true);
    toggleHideContainers(document.querySelectorAll(".bloc-tooggle"), true);
    setValueById(`${resourceName}_internship_address_manual_enter`, true);
    toggleHideContainers(document.querySelectorAll(".show-when-manual"), true);
    toggleHideContainers(document.querySelectorAll(".hide-when-manual"), false);

    const labelEntrepriseName = document.querySelector(
      `label[for='${resourceName}_employer_name']`
    );
    labelEntrepriseName.innerHTML =
      "Saisissez le nom (raison sociale) de votre établissement *";
    const inputEntrepriseName = document.getElementById(
      `${resourceName}_employer_name`
    );
    const sectorBloc = document.getElementById(
      `${resourceName}_sector_id-block`
    );
    inputEntrepriseName.required = true;
    inputEntrepriseName.removeAttribute("readonly");
    // hide the ministry choice block only if not public
    const ministrySelect = document.getElementById("group-choice");
    const ministryBlock = document.getElementById("ministry-block");
    const sectorSelect = document.getElementById(`${resourceName}_sector_id`);
    // Le secteur est désormais proposé pour le public ET le privé ;
    // seule l'option "Fonction publique" est réservée au public.
    if (!lastPublicValue) {
      // Private
      ministryBlock.classList.add("fr-hidden");
      ministrySelect.required = false;

      sectorBloc.hidden = false;
      toggleFonctionPubliqueOption(sectorSelect, false);
    } else {
      // Public company
      ministryBlock.classList.remove("fr-hidden");
      ministrySelect.setAttribute("required", "true");
      ministrySelect.required = true;

      sectorBloc.hidden = false;
      toggleFonctionPubliqueOption(sectorSelect, true);
    }

    const labelEntrepriseAddress = document.querySelector(
      `label[for='${resourceName}_entreprise_chosen_full_address']`
    );
    labelEntrepriseAddress.innerHTML =
      "Saisissez l'adresse du siège de votre établissement *";
    const inputEntrepriseAddress = document.getElementById(
      `${resourceName}_entreprise_chosen_full_address`
    );
    inputEntrepriseAddress.required = true;
    inputEntrepriseAddress.removeAttribute("readonly");
    inputEntrepriseName.addEventListener("keyup", (event) => {
      broadcast(employerNameChanged({ employerName: event.target.value }));
    });
  };

  const isAValidSiret = (siret) => {
    if (siret.length != 14 || isNaN(siret)) {
      return false;
    }

    // le SIRET est un numérique à 14 chiffres dont le dernier chiffre est une clef de LUHN.
    let sum = 0;
    let tmp;
    for (let index = 0; index < siret.length; index++) {
      tmp = parseInt(siret.charAt(index), 10);
      if (index % 2 == 0) {
        tmp *= 2;
        tmp = tmp > 9 ? tmp - 9 : tmp;
      }
      sum += tmp;
    }
    return sum % 10 === 0;
  };

  const onChange = (selection) => {
    show_form(true);
    setBlurError(null);
    // is_public is known when user searched by name and unknown when user searched by siret
    const is_public = selection.is_public;
    const zipcode = selection.adresseEtablissement.codePostalEtablissement;
    const city = selection.adresseEtablissement.libelleCommuneEtablissement;
    const street = `${selection.adresseEtablissement.numeroVoieEtablissement} ${selection.adresseEtablissement.typeVoieEtablissement} ${selection.adresseEtablissement.libelleVoieEtablissement} `;
    const addressConcatenated = `${street} ${zipcode} ${city}`.trim();
    searchCoordinatesByAddress(addressConcatenated);
    const employerName = selection.uniteLegale.denominationUniteLegale;
    setEmployerNameStr(employerName);

    setValueById(
      `${resourceName}_entreprise_full_address`,
      addressConcatenated
    );
    setValueById(
      `${resourceName}_entreprise_chosen_full_address`,
      addressConcatenated
    );
    setValueById(`${resourceName}_siret`, selection.siret);
    setValueById(
      `${resourceName}_presentation_siret`,
      siretPresentation(selection.siret)
    );
    setValueById(`${resourceName}_employer_name`, employerName);

    // Set the code APE if available in the selection
    if (selection.codeApe) {
      setCodeApe(selection.codeApe);
    } else {
      console.log("no code APE found");
    }

    broadcast(employerNameChanged({ employerName }));

    const ministry = document.getElementById("ministry-block");
    const ministryClassList = ministry.classList;
    const sectorBloc = document.getElementById(
      `${resourceName}_sector_id-block`
    );
    const sectorBlocClassList = sectorBloc.classList;
    const sector = document.getElementById(`${resourceName}_sector_id`);
    // TODO pub/sub with broadcasting would be better
    // because both jsx components and stimulus send events to the containers (show/hide)
    ministryClassList.add("fr-hidden"); // default

    const groupChoice = document.getElementById("group-choice");
    if (is_public != undefined) {
      // afficher le choix public/privé (l'utilisateur doit pouvoir le voir/le modifier)
      toggleHideContainerById("public-private-radio-buttons", true);
      if (is_public) {
        document.getElementById(`${resourceName}_is_public_true`).checked = true;
        // ministère de tutelle obligatoire pour une structure publique
        ministry.removeAttribute("style");
        ministryClassList.remove("fr-hidden");
        ministry.removeAttribute("hidden");
        ministry.hidden = false;
        if (groupChoice) groupChoice.required = true;

        // une structure publique choisit librement son secteur ("Fonction publique"
        // incluse) : on affiche le bloc secteur sans forcer de valeur
        sectorBlocClassList.remove("fr-hidden");
        sectorBloc.hidden = false;
        if (sector) {
          toggleFonctionPubliqueOption(sector, true);
          sector.setAttribute("required", "true");
          sector.value = "";
        }
      } else {
        // For private companies
        document.getElementById(
          `${resourceName}_is_public_false`
        ).checked = true;
        if (groupChoice) {
          groupChoice.required = false;
          groupChoice.value = "";
        }
        sectorBlocClassList.remove("fr-hidden");
        sectorBloc.hidden = false;
        if (sector) {
          // une structure privée ne peut pas choisir "Fonction publique"
          toggleFonctionPubliqueOption(sector, false);
          sector.setAttribute("required", "true");
          // pré-sélection du secteur depuis le mapping NAF si disponible
          if (selection.sectorId) {
            sector.value = selection.sectorId;
          } else {
            sector.value = "";
          }
        }
      }
    }
    if (isFaulty && formerPublicValue) {
      ministry.removeAttribute("style");
      ministryClassList.remove("fr-hidden");
    }
  };

  const clearImmediate = () => {
    setEmployerNameStr("");
    setSiret("");
    setSearchResults([]);
    setValueById(`${resourceName}_siret`, "");
    setValueById(`${resourceName}_presentation_siret`, "");
    setValueById(`${resourceName}_employer_name`, "");
    setValueById(`${resourceName}_entreprise_full_address`, "");
    setValueById(`${resourceName}_entreprise_chosen_full_address`, "");
    setValueById(`${resourceName}_entreprise_coordinates_longitude`, "");
    setValueById(`${resourceName}_entreprise_coordinates_latitude`, "");
    setValueById(`${resourceName}_code_ape`, "");
    show_form(false);
    broadcast(employerNameChanged({ employerName: "" }));
  };

  const show_form = (show) => {
    toggleHideContainers(document.querySelectorAll(".bloc-tooggle"), show);
    // hide Siren helper
    // toggleHideContainerById("input-siren", show && !newRecord);
  };

  const setCodeApe = (codeApe) => {
    setValueById(`${resourceName}_code_ape`, codeApe);
  };

  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSiret(siret);
    }, 600); // 600 ms

    return () => {
      clearTimeout(timerId);
    };
  }, [siret]);

  useEffect(() => {
    document.getElementById("siren-error").classList.add("fr-hidden");

    const cleanSiret = siret.replace(/\s/g, "");
    const cleanSiretIsNumeric = /^\d{2,}$/.test(cleanSiret);
    if (isAValidSiret(cleanSiret)) {
      searchCompanyBySiret(cleanSiret);
    } else if (cleanSiretIsNumeric) {
      setSearchResults([]);
    } else if (siret.length > 2) {
      searchCompanyByName(siret);
    }
  }, [debouncedSiret]);

  // initialization
  useEffect(() => {
    show_form(!newRecord);
    if (internshipAddressManualEnter || !newRecord) {
      setOpenManual();
    }
  }, []);

  const kinderGartenNote = (
    <div className="mt-2 d-flex ">
      <small>
        <span
          className="fr-icon-info-fill text-blue-info"
          aria-hidden="true"
        ></span>
      </small>
      <small className="text-blue-info fr-mx-1w align-items-center">
        <strong>
          Si vous êtes une école maternelle, élémentaire ou primaire publique,{" "}
        </strong>
        vous trouverez le numéro de SIRET de votre école dans votre application
        ONDE, dans l’onglet « École », dans l’écran « Carte d’identité », dans
        la rubrique « Identification ».
      </small>
    </div>
  );

  const siretTooltip = (
    <span>
      <button
        className="fr-btn fr-btn--tooltip"
        aria-describedby="tooltip"
        type="button"
      >
        infobulle
      </button>
      <span className="fr-tooltip fr-placement" id="tooltip" role="tooltip">
        Cette information nous aide à préremplir certains champs et est utile
        à des fins statistiques
      </span>
    </span>
  );

  const siretIndications = (
    <div>
      {kinderGartenNote}
      <div className="mt-2 d-flex align-items-center">
        <small>
          <span
            className="fr-icon-info-fill text-blue-info"
            aria-hidden="true"
          ></span>
        </small>
        <small className="text-blue-info fr-mx-1w">
          Structure introuvable ?
        </small>
        <a
          href="#"
          className="small text-blue-info"
          onClick={openManual}
        >
          Ajouter votre structure manuellement
        </a>
      </div>
    </div>
  );

  return (
    <div className="form-group" id="input-siren">
      <div className="container-downshift">
        <Downshift
          onChange={onChange}
          itemToString={(item) => (item ? item.value : "")}
        >
          {({
            getInputProps,
            getItemProps,
            getLabelProps,
            getMenuProps,
            isOpen,
            inputValue,
            highlightedIndex,
            selectedItem,
            getRootProps,
          }) => (
            <div>
              <label
                {...getLabelProps({
                  className: "fr-label",
                  htmlFor: `${resourceName}_siren`,
                })}
              >
                Indiquez le nom ou le SIRET de la structure d'accueil *{" "}
                {siretTooltip}
                {railsEnv === "development"
                  ? " (dev only : 21950572400209)"
                  : ""}
              </label>
              <div className="d-flex flew-row">
                <div className="input-group input-siren">
                  <input
                    {...getInputProps({
                      onChange: inputChange,
                      onBlur: handleBlur,
                      value: employerNameStr,
                      className: `fr-input${blurError ? " fr-input--error" : ""}`,
                      maxLength: 140,
                      id: `${resourceName}_siren`,
                      placeholder:
                        "Rechercher par nom ou par SIRET(14 caractères)",
                      name: `${resourceName}[siren]`,
                    })}
                  />
                </div>

                <div>
                  <button
                    className="fr-btn fr-btn--secondary fr-icon-delete-line"
                    aria-label="Effacer la recherche de structure"
                    title="Effacer la recherche de structure"
                    onClick={clearImmediate}
                  ></button>
                </div>
              </div>
              {blurError && (
                <p
                  className="fr-error-text"
                  id={`${resourceName}_siren-blur-error`}
                >
                  {blurError}
                </p>
              )}
              <p
                className="fr-error-text siren-error fr-hidden"
                id="siren-error"
                role="alert"
              >
                Aucune réponse trouvée, essayez avec le SIRET.
              </p>
              {!isOpen && siretIndications}
              <div className="search-in-sirene bg-white shadow">
                <ul
                  {...getMenuProps({
                    className: "p-0 m-0",
                  })}
                >
                  {isOpen
                    ? searchResults.map((item, index) => (
                        <li
                          {...getItemProps({
                            className: `fr-px-2w fr-py-2w listview-item ${
                              highlightedIndex === index
                                ? "highlighted-listview-item"
                                : ""
                            }`,
                            key: index,
                            index,
                            item,
                            style: {
                              fontWeight:
                                highlightedIndex === index ? "bold" : "normal",
                            },
                          })}
                        >
                          <p className="fr-my-0 text-dark font-weight-bold">
                            {item.uniteLegale.denominationUniteLegale}
                          </p>
                          <p className="fr-my-0 fr-text--sm text-dark">
                            {item.activite}
                          </p>
                          <p className="fr-my-0 fr-text--sm text-dark">
                            {
                              item.adresseEtablissement
                                .adresseCompleteEtablissement
                            }
                          </p>
                        </li>
                      ))
                    : null}
                </ul>
              </div>
            </div>
          )}
        </Downshift>
      </div>
    </div>
  );
}
